import { Stack } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useConditionByOrganization } from "@/services/fhir/condition/hooks/useConditionByOrganization";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useDict } from "@/utils/useDict";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import {
  buildConditionInsightCharts,
  buildConditionPatientRows,
} from "src-next/modules/clinical-insights/transformers/mapConditionInsights";
import { useFhirInsightLoadPhase } from "src-next/modules/clinical-insights/hooks/useFhirInsightLoadPhase";
import { matchesPractitionerFilter } from "src-next/modules/clinical-insights/helpers/filterResourcesByPractitioner";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { useInsightEmptyLabels } from "src-next/modules/clinical-insights/components/workspace/config/insightEmptyLabels";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";

const texts = getInsightAnalysisTexts("diagnosticos");

export function DiagnosticAnalysis({
  count,
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
}: ClinicalInsightChildProps & { count?: number }) {
  const { organizationId } = useOrganizationContext();
  const practitionerFilter = practitionerId || "all";
  const dict = useDict;

  const verificationLabel = useCallback(
    (code: string) => dict.verificationStatus[code] || code,
    [dict.verificationStatus]
  );

  const {
    data: conditionData,
    loading: conditionLoading,
    fetchConditions,
  } = useConditionByOrganization({
    organizationId: organizationId || "",
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    count: count || 250,
    includeEncounter: false,
    includePatient: true,
  });

  useEffect(() => {
    if (organizationId && isActive) fetchConditions();
  }, [organizationId, dateRange?.startDate, dateRange?.endDate, isActive, fetchConditions]);

  const { hasData } = useFhirInsightLoadPhase(
    Boolean(organizationId) && isActive,
    conditionLoading,
    onLoadPhaseChange
  );

  const conditions = useMemo(() => {
    const list = conditionData?.conditions || [];
    if (practitionerFilter === "all") return list;
    return list.filter((cond) =>
      matchesPractitionerFilter(practitionerFilter, cond.recorder?.reference)
    );
  }, [conditionData?.conditions, practitionerFilter]);
  const patients = conditionData?.patients || [];

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;
  const { emptyPeriodLabel, emptyPractitionerLabel } = useInsightEmptyLabels(
    dateRange,
    practitionerFilter,
    practitioner,
    periodDisplayLabel
  );

  const { statusItems, rankingItems, total } = useMemo(
    () => buildConditionInsightCharts(conditions, verificationLabel),
    [conditions, verificationLabel]
  );

  const patientRows = useMemo(
    () => buildConditionPatientRows(conditions, patients, verificationLabel),
    [conditions, patients, verificationLabel]
  );

  const statusLabelByKey = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of statusItems) {
      map[item.key] = item.label;
    }
    return map;
  }, [statusItems]);

  const statusInsightTitle = useMemo(() => {
    if (statusItems.length === 0) return "";
    const sorted = [...statusItems].sort((a, b) => b.value - a.value);
    const percentage = total > 0 ? Math.round((sorted[0].value / total) * 100) : 0;
    return `${percentage}% dos ${texts.itemPlural} estão como '${sorted[0].label}'`;
  }, [statusItems, total]);

  const loadPhase = conditionLoading ? "records" : "done";

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#2196f3"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={loadPhase}
        patientsProgress={{ loaded: 0, total: 0 }}
        hasData={hasData || conditions.length > 0}
        isEmpty={total === 0 && !conditionLoading}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={() => fetchConditions()}
        statusChart={{ insightText: statusInsightTitle, items: statusItems }}
        rankingChart={{ insightText: texts.rankingHint, items: rankingItems }}
        patientRows={patientRows}
        statusLabelByKey={statusLabelByKey}
        onPatientStatsChange={onPatientStatsChange}
      />
    </Stack>
  );
}
